<script setup lang="ts">
import { useAppState } from '../../store/state';
import { useDeviceApi } from '@wision/api';
import { Relay, RelayToggleButton } from '@wision/ui';
import { extractMeta } from '../../utilities/utils';
import type { SensorViewValues } from '../../models/sensorViewValues';
import { computed } from 'vue';
import type { PropType } from 'vue';
import { toast } from 'vue-sonner';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const appStore = useAppState();

const deviceApi = useDeviceApi(import.meta.env.VITE_WISION_API_HOST);

const props = defineProps({
  viewValues: {
    type: Object as PropType<SensorViewValues>,
    required: true,
  },
});

const poll = async () => {
  const [value, error] = await deviceApi.getRelay(Number(props.viewValues.deviceId));

  if (error) throw new Error();
  else return value;
};

const relayChannels = props.viewValues.values
  .filter(value => value.type === 'switch' && value.unit === 'relay')
  .map(relay => !!relay.value);

const setState = async (state: Array<boolean>) => {
  if (!appStore.hasAccess) return;

  deviceApi.setRelay(Number(props.viewValues.deviceId), state);
};

const clickHandler = (click: () => void) => () => {
  if (!appStore.hasAccess)
    toast.info(t('nfc.youNeedToLogin'));
  else click();
};

const meta = computed(() => extractMeta(props.viewValues));
</script>

<template>
  <div>
    <Relay
      :channels="relayChannels"
      :on-poll="poll"
      :meta="meta"
      @set-state="setState"
    >
      <template #button="{ pending, click, state }">
        <RelayToggleButton
          v-if="state"
          :state="state"
          :click="clickHandler(click)"
          :pending="pending"
        />
      </template>
    </Relay>
  </div>
</template>
